/**
 * Lecteur de codes QR (ISO/IEC 18004:2015) -- pur, zero E/S, zero dependance : de quoi lire sur une capture d'ecran le
 * code d'activation d'une double authentification (`otpauth://totp/...`) ou d'un reseau Wi-Fi, sans que l'image ni la
 * graine qu'elle porte ne quittent le poste.
 *
 * Pourquoi ecrit ici et pas importe : le texte lu EST le secret (graine TOTP, cle Wi-Fi) ; une bibliotheque de lecture
 * le verrait passer en clair. Celle-ci tient en un fichier relu ligne a ligne (`docs/DEPENDENCY-POLICY.md`), ecrit A
 * PART de l'encodeur (`qr-code.ts`) sans rien lui emprunter : les tests lisent les codes de l'encodeur ET ceux d'un
 * encodeur de reference (segno), une erreur partagee ne pourrait pas se cacher. Ce n'est PAS de la cryptographie :
 * Reed-Solomon n'est ici qu'un code correcteur d'erreurs.
 *
 * Perimetre : versions 1 a 40, niveaux L / M / Q / H, modes numerique, alphanumerique, octet, kanji, ECI ; erreurs
 * corrigees jusqu'a la capacite du bloc ; code tourne, en miroir, clair sur fond sombre (mode sombre). L'image est une
 * capture ou un fichier : la projection est affine (pas de perspective d'appareil photo).
 */

/** Image en niveaux de gris : `luminance[y * width + x]`, 0 = noir, 255 = blanc. */
export interface LuminanceImage {
  readonly width: number;
  readonly height: number;
  readonly luminance: Uint8Array;
}

export type QrReadResult = { readonly ok: true; readonly text: string; readonly version: number } | { readonly ok: false; readonly problem: string };

export const QR_NOT_FOUND = 'Aucun code QR trouve dans l’image.';
export const QR_UNREADABLE = 'Un code QR est visible mais illisible (image trop petite, floue ou coupee ?).';

// --- Tables (ISO/IEC 18004:2015, tableau 9), index [niveau][version], niveaux dans l'ordre L, M, Q, H ---

const ECC_PER_BLOCK: readonly (readonly number[])[] = [
  [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28],
  [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
];

const BLOCK_COUNT: readonly (readonly number[])[] = [
  [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25],
  [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49],
  [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68],
  [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81],
];

/** Bits de niveau du format (7.9.1) : L = 01, M = 00, Q = 11, H = 10 -> index L, M, Q, H. */
const LEVEL_OF_FORMAT_BITS = [1, 0, 3, 2];

/** Modules de donnees et de correction d'une version (tout sauf les motifs fixes, format et version). */
function rawDataModules(version: number): number {
  let result = (16 * version + 128) * version + 64;
  if (version >= 2) {
    const align = Math.floor(version / 7) + 2;
    result -= (25 * align - 10) * align - 55;
    if (version >= 7) result -= 36;
  }
  return result;
}

/** Centres des motifs d'alignement (annexe E) : 6, puis un pas regulier jusqu'a `taille - 7`. */
function alignmentCenters(version: number): number[] {
  if (version === 1) return [];
  const count = Math.floor(version / 7) + 2;
  const step = Math.floor((version * 8 + count * 3 + 5) / (count * 4 - 4)) * 2;
  const result = [6];
  for (let position = version * 4 + 10; result.length < count; position -= step) result.splice(1, 0, position);
  return result;
}

// --- Corps de Galois GF(256), polynome 0x11D ---

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
{
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255]!;
}

const mul = (a: number, b: number): number => (a === 0 || b === 0 ? 0 : EXP[LOG[a]! + LOG[b]!]!);
const div = (a: number, b: number): number => (a === 0 ? 0 : EXP[LOG[a]! + 255 - LOG[b]!]!);

/** Valeur en `x` d'un polynome rangé du degre 0 au plus haut. */
function evaluate(poly: readonly number[], x: number): number {
  let result = 0;
  for (let i = poly.length - 1; i >= 0; i--) result = mul(result, x) ^ poly[i]!;
  return result;
}

/**
 * Corrige un bloc (donnees puis correction, octet 0 = plus haut degre ; racines α^0 .. α^(ecc-1), celles de
 * l'encodeur) : syndromes, Berlekamp-Massey, recherche de Chien, Forney. Rend le nombre d'octets corriges, ou -1 si
 * le bloc a plus d'erreurs que le code n'en corrige -- jamais un bloc « corrige » au hasard.
 */
export function correctBlock(block: Uint8Array, ecc: number): number {
  const n = block.length;
  const syndromes: number[] = [];
  let clean = true;
  for (let j = 0; j < ecc; j++) {
    let value = 0;
    for (let i = 0; i < n; i++) value = mul(value, EXP[j]!) ^ block[i]!;
    syndromes.push(value);
    if (value !== 0) clean = false;
  }
  if (clean) return 0;

  // Berlekamp-Massey : polynome localisateur `locator` (degre 0 d'abord).
  let locator = [1];
  let previous = [1];
  let degree = 0;
  let shift = 1;
  let lastDiscrepancy = 1;
  for (let k = 0; k < ecc; k++) {
    let discrepancy = syndromes[k]!;
    for (let i = 1; i <= degree; i++) discrepancy ^= mul(locator[i] ?? 0, syndromes[k - i]!);
    if (discrepancy === 0) {
      shift++;
      continue;
    }
    const factor = div(discrepancy, lastDiscrepancy);
    const next = [...locator];
    for (let i = 0; i < previous.length; i++) {
      while (next.length <= i + shift) next.push(0);
      next[i + shift]! ^= mul(factor, previous[i]!);
    }
    if (2 * degree <= k) {
      previous = locator;
      degree = k + 1 - degree;
      lastDiscrepancy = discrepancy;
      shift = 1;
    } else {
      shift++;
    }
    locator = next;
  }
  while (locator.length > 1 && locator[locator.length - 1] === 0) locator.pop();
  if (degree * 2 > ecc || locator.length - 1 !== degree) return -1;

  // Chien : l'octet d'index `k` porte la puissance `n - 1 - k` ; il est faux si Λ(α^-(n-1-k)) = 0.
  const positions: number[] = [];
  for (let k = 0; k < n; k++) {
    const power = n - 1 - k;
    if (evaluate(locator, EXP[(255 - power) % 255]!) === 0) positions.push(k);
  }
  if (positions.length !== degree) return -1;

  // Forney (premiere racine α^0) : e = X · Ω(X^-1) / Λ'(X^-1), Ω = S·Λ mod x^ecc.
  const evaluator = new Array<number>(ecc).fill(0);
  for (let i = 0; i < ecc; i++) {
    for (let j = 0; j < locator.length && i + j < ecc; j++) evaluator[i + j]! ^= mul(syndromes[i]!, locator[j]!);
  }
  const derivative = locator.slice(1).map((coefficient, i) => (i % 2 === 0 ? coefficient : 0));
  for (const k of positions) {
    const power = n - 1 - k;
    const inverse = EXP[(255 - power) % 255]!;
    const denominator = evaluate(derivative, inverse);
    if (denominator === 0) return -1;
    block[k]! ^= mul(EXP[power]!, div(evaluate(evaluator, inverse), denominator));
  }
  for (let j = 0; j < ecc; j++) {
    let value = 0;
    for (let i = 0; i < n; i++) value = mul(value, EXP[j]!) ^ block[i]!;
    if (value !== 0) return -1;
  }
  return positions.length;
}

// --- Format et version ---

function bitCount(value: number): number {
  let count = 0;
  for (let v = value; v !== 0; v &= v - 1) count++;
  return count;
}

/** Les 32 mots de format valides (BCH(15,5), masque 101010000010010), index = niveau << 3 | masque. */
const FORMAT_WORDS: readonly number[] = Array.from({ length: 32 }, (_, data) => {
  let remainder = data;
  for (let i = 0; i < 10; i++) remainder = (remainder << 1) ^ ((remainder >>> 9) * 0x537);
  return ((data << 10) | (remainder & 0x3ff)) ^ 0x5412;
});

/** Mots de version valides (BCH(18,6)), versions 7 a 40. */
const VERSION_WORDS: readonly number[] = Array.from({ length: 41 }, (_, version) => {
  let remainder = version;
  for (let i = 0; i < 12; i++) remainder = (remainder << 1) ^ ((remainder >>> 11) * 0x1f25);
  return (version << 12) | (remainder & 0xfff);
});

/** Mot valide le plus proche des lectures (distance de Hamming 3 au plus : ce que le code BCH corrige). */
function nearest(words: readonly number[], readings: readonly number[], first: number): number | null {
  let best: number | null = null;
  let bestDistance = 4;
  for (let index = first; index < words.length; index++) {
    for (const reading of readings) {
      const distance = bitCount(words[index]! ^ reading);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    }
  }
  return best;
}

const MASKS: readonly ((row: number, col: number) => boolean)[] = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

/** Modules des motifs fixes, du format et de la version : ni donnees ni masque. */
function functionModules(version: number): boolean[][] {
  const size = version * 4 + 17;
  const reserved = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
  const reserve = (r: number, c: number): void => {
    if (r >= 0 && c >= 0 && r < size && c < size) reserved[r]![c] = true;
  };
  for (const [top, left] of [[0, 0], [0, size - 7], [size - 7, 0]] as const) {
    for (let r = -1; r <= 7; r++) for (let c = -1; c <= 7; c++) reserve(top + r, left + c);
  }
  for (let i = 0; i < size; i++) {
    reserve(6, i);
    reserve(i, 6);
  }
  for (let i = 0; i <= 8; i++) {
    reserve(8, i);
    reserve(i, 8);
  }
  for (let i = 0; i < 8; i++) {
    reserve(8, size - 1 - i);
    reserve(size - 1 - i, 8);
  }
  const centers = alignmentCenters(version);
  const last = centers.length - 1;
  centers.forEach((r, i) =>
    centers.forEach((c, j) => {
      if ((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0)) return;
      for (let dr = -2; dr <= 2; dr++) for (let dc = -2; dc <= 2; dc++) reserve(r + dr, c + dc);
    }),
  );
  if (version >= 7) {
    for (let i = 0; i < 18; i++) {
      reserve(Math.floor(i / 3), size - 11 + (i % 3));
      reserve(size - 11 + (i % 3), Math.floor(i / 3));
    }
  }
  return reserved;
}

// --- Flux de donnees ---

const ALPHANUMERIC = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ $%*+-./:';

/** Jeux de caracteres ECI (AIM ECI) lus ici ; un autre designateur rend le code illisible plutot que deforme. */
const ECI_CHARSETS: Readonly<Record<number, string>> = {
  1: 'iso-8859-1', 3: 'iso-8859-1', 4: 'iso-8859-2', 5: 'iso-8859-3', 6: 'iso-8859-4', 7: 'iso-8859-5',
  8: 'iso-8859-6', 9: 'iso-8859-7', 10: 'iso-8859-8', 11: 'iso-8859-9', 13: 'iso-8859-11', 15: 'iso-8859-13',
  16: 'iso-8859-14', 17: 'iso-8859-15', 18: 'iso-8859-16', 20: 'shift_jis', 21: 'windows-1250', 22: 'windows-1251',
  23: 'windows-1252', 24: 'windows-1256', 25: 'utf-16be', 26: 'utf-8', 27: 'us-ascii', 28: 'big5', 29: 'gb18030',
  30: 'euc-kr',
};

class BitReader {
  private position = 0;
  constructor(private readonly bytes: Uint8Array) {}

  get remaining(): number {
    return this.bytes.length * 8 - this.position;
  }

  read(length: number): number {
    if (length > this.remaining) throw new Error('flux tronque');
    let value = 0;
    for (let i = 0; i < length; i++) {
      value = (value << 1) | ((this.bytes[this.position >>> 3]! >>> (7 - (this.position & 7))) & 1);
      this.position++;
    }
    return value;
  }
}

function decodeBytes(bytes: Uint8Array, charset: string | null): string {
  if (charset !== null) return new TextDecoder(charset, { fatal: true }).decode(bytes);
  // Sans ECI la norme dit ISO-8859-1, mais les encodeurs courants ecrivent de l'UTF-8 sans le dire : un UTF-8 valide
  // est lu comme tel (un texte latin-1 accentue n'est presque jamais un UTF-8 valide).
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('iso-8859-1').decode(bytes);
  }
}

/** Texte des octets de donnees corriges ; leve une erreur sur tout flux hors norme. */
function decodeSegments(data: Uint8Array, version: number): string {
  const reader = new BitReader(data);
  const tier = version <= 9 ? 0 : version <= 26 ? 1 : 2;
  let text = '';
  let charset: string | null = null;
  while (reader.remaining >= 4) {
    const mode = reader.read(4);
    if (mode === 0b0000) break;
    if (mode === 0b0001) {
      let count = reader.read([10, 12, 14][tier]!);
      while (count >= 3) {
        const value = reader.read(10);
        if (value >= 1000) throw new Error('chiffres hors plage');
        text += String(value).padStart(3, '0');
        count -= 3;
      }
      if (count === 2) {
        const value = reader.read(7);
        if (value >= 100) throw new Error('chiffres hors plage');
        text += String(value).padStart(2, '0');
      } else if (count === 1) {
        const value = reader.read(4);
        if (value >= 10) throw new Error('chiffre hors plage');
        text += String(value);
      }
    } else if (mode === 0b0010) {
      let count = reader.read([9, 11, 13][tier]!);
      while (count >= 2) {
        const value = reader.read(11);
        if (value >= 45 * 45) throw new Error('alphanumerique hors plage');
        text += ALPHANUMERIC[Math.floor(value / 45)]! + ALPHANUMERIC[value % 45]!;
        count -= 2;
      }
      if (count === 1) {
        const value = reader.read(6);
        if (value >= 45) throw new Error('alphanumerique hors plage');
        text += ALPHANUMERIC[value]!;
      }
    } else if (mode === 0b0100) {
      const count = reader.read([8, 16, 16][tier]!);
      const bytes = new Uint8Array(count);
      for (let i = 0; i < count; i++) bytes[i] = reader.read(8);
      text += decodeBytes(bytes, charset);
    } else if (mode === 0b1000) {
      const count = reader.read([8, 10, 12][tier]!);
      const bytes = new Uint8Array(count * 2);
      for (let i = 0; i < count; i++) {
        const value = reader.read(13);
        const packed = Math.floor(value / 0xc0) * 0x100 + (value % 0xc0);
        const code = packed + (packed < 0x1f00 ? 0x8140 : 0xc140);
        bytes[2 * i] = code >>> 8;
        bytes[2 * i + 1] = code & 0xff;
      }
      text += new TextDecoder('shift_jis', { fatal: true }).decode(bytes);
    } else if (mode === 0b0111) {
      const first = reader.read(8);
      const designator =
        (first & 0x80) === 0 ? first : (first & 0xc0) === 0x80 ? ((first & 0x3f) << 8) | reader.read(8) : (first & 0xe0) === 0xc0 ? ((first & 0x1f) << 16) | reader.read(16) : -1;
      const label = ECI_CHARSETS[designator];
      if (label === undefined) throw new Error(`ECI ${designator} non pris en charge`);
      charset = label;
    } else if (mode === 0b0011) {
      reader.read(16); // Ajout structure : rang, total et parite ; seul ce symbole est lu.
    } else if (mode === 0b0101) {
      // FNC1 en premiere position (GS1) : aucun champ.
    } else if (mode === 0b1001) {
      reader.read(8); // FNC1 en seconde position : indicateur d'application.
    } else {
      throw new Error(`mode ${mode.toString(2)} inconnu`);
    }
  }
  return text;
}

/**
 * Lit une matrice de modules (`true` = sombre, `modules[ligne][colonne]`) : format (copie la plus proche d'un mot
 * valide), version (lue des la version 7, sinon deduite de la taille), demasquage, desentrelacement, correction de
 * chaque bloc, flux de donnees. `null` si la matrice n'est pas un code lisible.
 */
export function decodeQrMatrix(modules: readonly (readonly boolean[])[]): { text: string; version: number } | null {
  const size = modules.length;
  if ((size - 17) % 4 !== 0 || size < 21 || size > 177 || modules.some((row) => row.length !== size)) return null;
  const dark = (r: number, c: number): number => (modules[r]![c] ? 1 : 0);

  let first = 0;
  for (const [r, c] of [[8, 0], [8, 1], [8, 2], [8, 3], [8, 4], [8, 5], [8, 7], [8, 8], [7, 8], [5, 8], [4, 8], [3, 8], [2, 8], [1, 8], [0, 8]] as const) first = (first << 1) | dark(r, c);
  let second = 0;
  for (let i = 0; i < 7; i++) second = (second << 1) | dark(size - 1 - i, 8);
  for (let i = 0; i < 8; i++) second = (second << 1) | dark(8, size - 8 + i);
  const format = nearest(FORMAT_WORDS, [first, second], 0);
  if (format === null) return null;
  const level = LEVEL_OF_FORMAT_BITS[format >>> 3]!;
  const mask = MASKS[format & 7]!;

  let version = (size - 17) / 4;
  if (version >= 7) {
    let topRight = 0;
    let bottomLeft = 0;
    for (let i = 17; i >= 0; i--) {
      topRight = (topRight << 1) | dark(Math.floor(i / 3), size - 11 + (i % 3));
      bottomLeft = (bottomLeft << 1) | dark(size - 11 + (i % 3), Math.floor(i / 3));
    }
    const read = nearest(VERSION_WORDS, [topRight, bottomLeft], 7);
    // Une version lue qui contredit la taille : ce n'est pas ce code (mauvaise grille d'echantillonnage).
    if (read === null || read !== version) return null;
    version = read;
  }

  const reserved = functionModules(version);
  const total = Math.floor(rawDataModules(version) / 8);
  const codewords = new Uint8Array(total);
  let bit = 0;
  let upward = true;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let step = 0; step < size; step++) {
      const r = upward ? size - 1 - step : step;
      for (const c of [right, right - 1]) {
        if (reserved[r]![c]) continue;
        if (bit < total * 8) {
          if ((dark(r, c) === 1) !== mask(r, c)) codewords[bit >>> 3]! |= 0x80 >>> (bit & 7);
          bit++;
        }
      }
    }
    upward = !upward;
  }

  const ecc = ECC_PER_BLOCK[level]![version]!;
  const blockCount = BLOCK_COUNT[level]![version]!;
  const shortCount = blockCount - (total % blockCount);
  const shortData = Math.floor(total / blockCount) - ecc;
  const dataLengths = Array.from({ length: blockCount }, (_, b) => shortData + (b < shortCount ? 0 : 1));
  const blocks = dataLengths.map((length) => new Uint8Array(length + ecc));
  let index = 0;
  for (let i = 0; i <= shortData; i++) dataLengths.forEach((length, b) => i < length && (blocks[b]![i] = codewords[index++]!));
  for (let i = 0; i < ecc; i++) blocks.forEach((block, b) => (block[dataLengths[b]! + i] = codewords[index++]!));
  for (const block of blocks) if (correctBlock(block, ecc) < 0) return null;

  const data = new Uint8Array(dataLengths.reduce((sum, length) => sum + length, 0));
  let offset = 0;
  blocks.forEach((block, b) => {
    data.set(block.subarray(0, dataLengths[b]), offset);
    offset += dataLengths[b]!;
  });
  try {
    return { text: decodeSegments(data, version), version };
  } catch {
    return null;
  } finally {
    data.fill(0);
    codewords.fill(0);
    for (const block of blocks) block.fill(0);
  }
}

// --- Localisation dans une image ---

type Bits = Uint8Array;

/** Seuil d'Otsu de l'histogramme : la separation sombre / clair d'une capture nette. */
function otsuThreshold(luminance: Uint8Array): number {
  const histogram = new Array<number>(256).fill(0);
  for (const value of luminance) histogram[value]!++;
  const total = luminance.length;
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * histogram[i]!;
  let background = 0;
  let backgroundSum = 0;
  let best = 0;
  let threshold = 127;
  for (let t = 0; t < 256; t++) {
    background += histogram[t]!;
    if (background === 0) continue;
    const foreground = total - background;
    if (foreground === 0) break;
    backgroundSum += t * histogram[t]!;
    const meanBackground = backgroundSum / background;
    const meanForeground = (sum - backgroundSum) / foreground;
    const between = background * foreground * (meanBackground - meanForeground) ** 2;
    if (between > best) {
      best = between;
      threshold = t;
    }
  }
  return threshold;
}

/** 1 = sombre. `global` : seuil d'Otsu ; `local` : plus sombre que la moyenne de son voisinage (fond inegal). */
function binarize(image: LuminanceImage, method: 'global' | 'local', inverted: boolean): Bits {
  const { width, height, luminance } = image;
  const bits = new Uint8Array(width * height);
  if (method === 'global') {
    const threshold = otsuThreshold(luminance);
    for (let i = 0; i < bits.length; i++) bits[i] = (luminance[i]! <= threshold) !== inverted ? 1 : 0;
    return bits;
  }
  const integral = new Float64Array((width + 1) * (height + 1));
  for (let y = 0; y < height; y++) {
    let row = 0;
    for (let x = 0; x < width; x++) {
      row += luminance[y * width + x]!;
      integral[(y + 1) * (width + 1) + x + 1] = integral[y * (width + 1) + x + 1]! + row;
    }
  }
  const radius = Math.max(8, Math.round(Math.min(width, height) / 8));
  for (let y = 0; y < height; y++) {
    const y0 = Math.max(0, y - radius);
    const y1 = Math.min(height, y + radius + 1);
    for (let x = 0; x < width; x++) {
      const x0 = Math.max(0, x - radius);
      const x1 = Math.min(width, x + radius + 1);
      const area = (x1 - x0) * (y1 - y0);
      const sum = integral[y1 * (width + 1) + x1]! - integral[y0 * (width + 1) + x1]! - integral[y1 * (width + 1) + x0]! + integral[y0 * (width + 1) + x0]!;
      const value = luminance[y * width + x]!;
      const mean = sum / area;
      bits[y * width + x] = (inverted ? value > mean + 8 : value < mean - 8) ? 1 : 0;
    }
  }
  return bits;
}

interface Finder {
  x: number;
  y: number;
  module: number;
  count: number;
}

/** Cinq series sombre / clair / sombre / clair / sombre dans le rapport 1:1:3:1:1 d'un repere (a 50 % pres). */
function finderRatio(runs: readonly number[]): boolean {
  const total = runs.reduce((sum, run) => sum + run, 0);
  if (total < 7) return false;
  const module = total / 7;
  const variance = module / 2;
  return (
    Math.abs(module - runs[0]!) < variance &&
    Math.abs(module - runs[1]!) < variance &&
    Math.abs(3 * module - runs[2]!) < 3 * variance &&
    Math.abs(module - runs[3]!) < variance &&
    Math.abs(module - runs[4]!) < variance
  );
}

/**
 * Series autour de (`x`, `y`) le long de (`dx`, `dy`) : centre sombre, puis clair, puis sombre de chaque cote ; rend
 * le centre du repere sur cet axe et la longueur totale, ou `null` si le rapport 1:1:3:1:1 n'y est pas.
 */
function crossCheck(bits: Bits, width: number, height: number, x: number, y: number, dx: number, dy: number, expected: number): { center: number; total: number } | null {
  const at = (step: number): number => {
    const px = x + dx * step;
    const py = y + dy * step;
    return px < 0 || py < 0 || px >= width || py >= height ? -1 : bits[py * width + px]!;
  };
  if (at(0) !== 1) return null;
  const limit = Math.ceil(expected * 2);
  const runs = [0, 0, 0, 0, 0];
  let step = 0;
  for (const [run, color] of [[2, 1], [1, 0], [0, 1]] as const) {
    while (at(step) === color && runs[run]! <= limit) {
      runs[run]!++;
      step--;
    }
    if (runs[run]! === 0 || runs[run]! > limit) return null;
  }
  const start = step + 1;
  step = 1;
  for (const [run, color] of [[2, 1], [3, 0], [4, 1]] as const) {
    while (at(step) === color && runs[run]! <= limit) {
      runs[run]!++;
      step++;
    }
    if (runs[run]! === 0 || runs[run]! > limit) return null;
  }
  if (!finderRatio(runs)) return null;
  const total = runs.reduce((sum, run) => sum + run, 0);
  if (5 * Math.abs(total - expected) >= 2 * expected) return null;
  return { center: start + runs[0]! + runs[1]! + runs[2]! / 2, total };
}

/** Reperes candidats : series de chaque ligne au rapport 1:1:3:1:1, recoupees verticalement puis horizontalement. */
function findFinders(bits: Bits, width: number, height: number): Finder[] {
  const finders: Finder[] = [];
  const runs: number[] = [];
  const starts: number[] = [];
  for (let y = 0; y < height; y++) {
    runs.length = 0;
    starts.length = 0;
    let x = 0;
    while (x < width) {
      const color = bits[y * width + x]!;
      const start = x;
      while (x < width && bits[y * width + x] === color) x++;
      runs.push(x - start);
      starts.push(start);
      if (runs.length >= 5 && bits[y * width + starts[runs.length - 5]!] === 1) {
        const window = runs.slice(-5);
        if (!finderRatio(window)) continue;
        const total = window.reduce((sum, run) => sum + run, 0);
        const centerX = Math.floor(starts[runs.length - 3]! + runs[runs.length - 3]! / 2);
        const vertical = crossCheck(bits, width, height, centerX, y, 0, 1, total);
        if (vertical === null) continue;
        const centerY = y + vertical.center;
        const horizontal = crossCheck(bits, width, height, centerX, Math.floor(centerY), 1, 0, total);
        if (horizontal === null) continue;
        const cx = centerX + horizontal.center;
        const module = (horizontal.total + vertical.total) / 14;
        const known = finders.find((f) => Math.abs(f.x - cx) <= module && Math.abs(f.y - centerY) <= module && Math.abs(f.module - module) <= Math.max(1, module / 2));
        if (known !== undefined) {
          const weight = known.count + 1;
          known.x = (known.x * known.count + cx) / weight;
          known.y = (known.y * known.count + centerY) / weight;
          known.module = (known.module * known.count + module) / weight;
          known.count = weight;
        } else if (finders.length < 256) {
          finders.push({ x: cx, y: centerY, module, count: 1 });
        }
      }
    }
  }
  return finders;
}

interface Placement {
  readonly topLeft: Finder;
  readonly topRight: Finder;
  readonly bottomLeft: Finder;
  readonly score: number;
}

/** Triplets de reperes formant un triangle rectangle isocele de modules semblables, du plus regulier au moins regulier. */
function placements(finders: readonly Finder[]): Placement[] {
  const confirmed = finders.filter((f) => f.count >= 2);
  const pool = (confirmed.length >= 3 ? confirmed : [...finders]).sort((a, b) => b.count - a.count).slice(0, 16);
  const result: Placement[] = [];
  const distance = (a: Finder, b: Finder): number => Math.hypot(a.x - b.x, a.y - b.y);
  for (let i = 0; i < pool.length; i++) {
    for (let j = i + 1; j < pool.length; j++) {
      for (let k = j + 1; k < pool.length; k++) {
        const trio = [pool[i]!, pool[j]!, pool[k]!];
        const modules = trio.map((f) => f.module);
        if (Math.max(...modules) > 1.5 * Math.min(...modules)) continue;
        // Le sommet de l'angle droit (repere haut-gauche) est oppose au plus grand cote.
        const sides = [distance(trio[1]!, trio[2]!), distance(trio[0]!, trio[2]!), distance(trio[0]!, trio[1]!)];
        const corner = sides.indexOf(Math.max(...sides));
        const topLeft = trio[corner]!;
        let [a, b] = trio.filter((_, n) => n !== corner) as [Finder, Finder];
        const legA = distance(topLeft, a);
        const legB = distance(topLeft, b);
        const hypotenuse = sides[corner]!;
        const legs = Math.abs(legA - legB) / Math.max(legA, legB);
        const right = Math.abs(hypotenuse * hypotenuse - legA * legA - legB * legB) / (hypotenuse * hypotenuse);
        if (legs > 0.25 || right > 0.2) continue;
        // Haut-droit puis bas-gauche dans le sens des aiguilles d'une montre (y vers le bas).
        if ((a.x - topLeft.x) * (b.y - topLeft.y) - (a.y - topLeft.y) * (b.x - topLeft.x) < 0) [a, b] = [b, a];
        const module = (topLeft.module + a.module + b.module) / 3;
        if ((legA + legB) / 2 / module < 10) continue;
        result.push({ topLeft, topRight: a, bottomLeft: b, score: legs + right });
      }
    }
  }
  return result.sort((p, q) => p.score - q.score);
}

/**
 * Matrice de `size` modules echantillonnee par la projection affine des trois reperes (centre du repere = module 3,5).
 * Chaque module vote sur cinq points (centre et quart de module autour). `null` si la grille sort de l'image.
 */
function sample(bits: Bits, width: number, height: number, placement: Placement, size: number): boolean[][] | null {
  const { topLeft, topRight, bottomLeft } = placement;
  const span = size - 7;
  const ux = (topRight.x - topLeft.x) / span;
  const uy = (topRight.y - topLeft.y) / span;
  const vx = (bottomLeft.x - topLeft.x) / span;
  const vy = (bottomLeft.y - topLeft.y) / span;
  const offsets = [[0, 0], [-0.25, 0], [0.25, 0], [0, -0.25], [0, 0.25]] as const;
  const modules: boolean[][] = [];
  for (let r = 0; r < size; r++) {
    const row: boolean[] = [];
    for (let c = 0; c < size; c++) {
      let votes = 0;
      for (const [dc, dr] of offsets) {
        const mc = c + 0.5 + dc - 3.5;
        const mr = r + 0.5 + dr - 3.5;
        const px = Math.floor(topLeft.x + mc * ux + mr * vx);
        const py = Math.floor(topLeft.y + mc * uy + mr * vy);
        if (px < -1 || py < -1 || px > width || py > height) return null;
        const x = Math.min(width - 1, Math.max(0, px));
        const y = Math.min(height - 1, Math.max(0, py));
        votes += bits[y * width + x]!;
      }
      row.push(votes >= 3);
    }
    modules.push(row);
  }
  return modules;
}

/** Tailles a essayer : celle que la distance des reperes suggere, puis ses voisines (4V + 17). */
function candidateSizes(placement: Placement): number[] {
  const { topLeft, topRight, bottomLeft } = placement;
  const module = (topLeft.module + topRight.module + bottomLeft.module) / 3;
  const span = (Math.hypot(topRight.x - topLeft.x, topRight.y - topLeft.y) + Math.hypot(bottomLeft.x - topLeft.x, bottomLeft.y - topLeft.y)) / 2;
  const estimate = span / module + 7;
  const sizes: number[] = [];
  for (let version = 1; version <= 40; version++) sizes.push(version * 4 + 17);
  return sizes.filter((size) => Math.abs(size - estimate) <= 8).sort((a, b) => Math.abs(a - estimate) - Math.abs(b - estimate));
}

function transpose(modules: readonly (readonly boolean[])[]): boolean[][] {
  return modules.map((row, r) => row.map((_, c) => modules[c]![r]!));
}

/**
 * Image agrandie deux fois (bilineaire) : un module de 2 pixels decale d'un demi-pixel (capture mise a l'echelle par le
 * navigateur) laisse un pixel gris a chaque bord, que tout seuil range d'un cote ; agrandi, ce pixel se partage en un
 * sombre et un clair et le rapport 1:1:3:1:1 revient.
 */
function upscale(image: LuminanceImage): LuminanceImage {
  const { width, height, luminance } = image;
  const out = new Uint8Array(width * height * 4);
  const at = (x: number, y: number): number => luminance[Math.min(height - 1, Math.max(0, y)) * width + Math.min(width - 1, Math.max(0, x))]!;
  for (let y = 0; y < height * 2; y++) {
    const sy = y >>> 1;
    const ny = y & 1 ? sy + 1 : sy - 1;
    for (let x = 0; x < width * 2; x++) {
      const sx = x >>> 1;
      const nx = x & 1 ? sx + 1 : sx - 1;
      out[y * width * 2 + x] = Math.round(0.5625 * at(sx, sy) + 0.1875 * at(nx, sy) + 0.1875 * at(sx, ny) + 0.0625 * at(nx, ny));
    }
  }
  return { width: width * 2, height: height * 2, luminance: out };
}

/** Pixels au-dela desquels l'image n'est pas agrandie (16 millions une fois agrandie). */
const UPSCALE_LIMIT = 4_000_000;

function attempt(image: LuminanceImage, seen: { value: boolean }): { text: string; version: number } | null {
  const { width, height } = image;
  for (const method of ['global', 'local'] as const) {
    for (const inverted of [false, true]) {
      const bits = binarize(image, method, inverted);
      try {
        const found = placements(findFinders(bits, width, height));
        if (found.length > 0) seen.value = true;
        for (const placement of found.slice(0, 12)) {
          for (const size of candidateSizes(placement)) {
            const modules = sample(bits, width, height, placement, size);
            if (modules === null) continue;
            const decoded = decodeQrMatrix(modules) ?? decodeQrMatrix(transpose(modules));
            if (decoded !== null) return decoded;
          }
        }
      } finally {
        bits.fill(0);
      }
    }
  }
  return null;
}

/**
 * Lit le code QR d'une image (capture d'ecran, fichier) : seuil global puis local, sombre sur clair puis clair sur
 * sombre ; pour chaque triplet de reperes plausible, chaque taille voisine, la matrice puis son miroir ; enfin, si
 * rien n'est lu, l'image agrandie deux fois. Le premier code dont tous les blocs se corrigent est rendu.
 */
export function readQrImage(image: LuminanceImage): QrReadResult {
  const { width, height } = image;
  if (width < 21 || height < 21 || image.luminance.length !== width * height) return { ok: false, problem: QR_NOT_FOUND };
  const seen = { value: false };
  const decoded = attempt(image, seen);
  if (decoded !== null) return { ok: true, ...decoded };
  if (width * height <= UPSCALE_LIMIT) {
    const larger = upscale(image);
    try {
      const again = attempt(larger, seen);
      if (again !== null) return { ok: true, ...again };
    } finally {
      larger.luminance.fill(0);
    }
  }
  return { ok: false, problem: seen.value ? QR_UNREADABLE : QR_NOT_FOUND };
}
